import type {Icon as PrimerIcon} from '@primer/octicons-react'
import CircleOcticon from '@github-ui/circle-octicon'
import type React from 'react'

import styles from './InfobarPopover.module.css'
import {clsx} from 'clsx'

/**
 * This collection of components creates a consistent UI for the branch info bar.
 * Once can compose the layout in the following way:
 *
 * <PopoverContainer>
 *  <PopoverIcon>
 *   <Icon />
 *  </PopoverIcon>
 *  <PopoverContent
 *    icon={<PopoverIcon icon={AlertIcon} />}
 *    header={<Text>Popup header</Text>}
 *    content={<Text>Content of the popup</Text>}
 *  />
 *  <PopoverActionContainer>
 *   <Button>Discard</Button>
 *   <Button as={Link} className={clsx(linkButtonStyles.linkButton, 'flex-1')}>Update</Button>
 *  </PopoverActionContainer>
 * </PopoverContainer>
 *
 */
export function PopoverContainer({children}: {children: React.ReactNode}) {
  return <div className={clsx('popover-container-width', styles.Box)}>{children}</div>
}

interface PopoverContentProps {
  icon: React.ReactNode
  header: React.ReactNode
  headerRef?: React.RefObject<HTMLHeadingElement | null>
  content: React.ReactNode
}

export function PopoverContent({icon, header, content, headerRef}: PopoverContentProps) {
  return (
    <div className="d-flex p-3">
      <div className="mr-2">{icon}</div>
      <div>
        <h2 ref={headerRef} tabIndex={-1} className="f5 mb-1">
          {header}
        </h2>
        <span className="fgColor-muted f6">{content}</span>
      </div>
    </div>
  )
}

interface PopoverIconProps {
  success?: boolean
  icon: PrimerIcon
}
export function PopoverIcon({icon: IconComponent, success}: PopoverIconProps) {
  return (
    <CircleOcticon
      size={30}
      icon={() => <IconComponent size={16} />}
      className={clsx(styles.CircleOcticon, success && styles.success)}
    />
  )
}

export function PopoverActions({children}: {children: React.ReactNode}) {
  return <div className="d-flex flex-wrap flex-justify-between p-3 gap-3 border-top borderColor-muted">{children}</div>
}

try{ PopoverContainer.displayName ||= 'PopoverContainer' } catch {}
try{ PopoverContent.displayName ||= 'PopoverContent' } catch {}
try{ PopoverIcon.displayName ||= 'PopoverIcon' } catch {}
try{ PopoverActions.displayName ||= 'PopoverActions' } catch {}